import { api } from "./api";

export const LICENSED_SPONSOR_DISCLAIMER =
  "This means the company appears on the GOV.UK licensed sponsor register. It does not guarantee sponsorship for this specific role. Always confirm with the employer before applying.";

const emptyStatus = (companyName = "") => ({
  company_name: companyName,
  is_licensed_sponsor: false,
  matched_name: null,
  badge_text: null,
  source: "GOV.UK Register of licensed sponsors: workers",
  disclaimer: LICENSED_SPONSOR_DISCLAIMER,
});

const cache = new Map();
const pending = new Map();
let activeRequests = 0;
const queue = [];
const MAX_ACTIVE_REQUESTS = 4;

function normalizeCompanyName(companyName) {
  return String(companyName || "").trim().toLowerCase();
}

function schedule(task) {
  return new Promise((resolve, reject) => {
    queue.push({ task, resolve, reject });
    runNext();
  });
}

function runNext() {
  if (activeRequests >= MAX_ACTIVE_REQUESTS || !queue.length) return;

  const item = queue.shift();
  activeRequests += 1;

  item.task()
    .then(item.resolve)
    .catch(item.reject)
    .finally(() => {
      activeRequests -= 1;
      runNext();
    });
}

function normalizeStatus(companyName, data) {
  const confirmed = data?.is_licensed_sponsor === true;

  return {
    company_name: data?.company_name || companyName,
    is_licensed_sponsor: confirmed,
    matched_name: confirmed ? data?.matched_name || null : null,
    badge_text: confirmed ? "Licensed Sponsor" : null,
    source: data?.source || "GOV.UK Register of licensed sponsors: workers",
    disclaimer: data?.disclaimer || LICENSED_SPONSOR_DISCLAIMER,
  };
}

async function fetchLicensedSponsorStatus(companyName) {
  try {
    const { data } = await api.get(
      `/jobs/licensed-sponsor/${encodeURIComponent(companyName)}`,
      { timeout: 12000 }
    );

    return normalizeStatus(companyName, data);
  } catch {
    return emptyStatus(companyName);
  }
}

export async function getLicensedSponsorStatus(companyName) {
  const trimmed = String(companyName || "").trim();
  if (!trimmed) return emptyStatus("");

  const key = normalizeCompanyName(trimmed);
  if (cache.has(key)) return cache.get(key);
  if (pending.has(key)) return pending.get(key);

  const request = schedule(() => fetchLicensedSponsorStatus(trimmed)).then((status) => {
    cache.set(key, status);
    pending.delete(key);
    return status;
  });

  pending.set(key, request);
  return request;
}

export async function enrichJobsWithLicensedSponsorStatus(jobs) {
  const uniqueCompanies = Array.from(
    new Set((jobs || []).map((job) => job.company).filter(Boolean))
  );

  const statuses = await Promise.all(
    uniqueCompanies.map(async (companyName) => [
      normalizeCompanyName(companyName),
      await getLicensedSponsorStatus(companyName),
    ])
  );
  const statusMap = new Map(statuses);

  return (jobs || []).map((job) => ({
    ...job,
    licensedSponsorStatus:
      statusMap.get(normalizeCompanyName(job.company)) || emptyStatus(job.company),
  }));
}

export function clearLicensedSponsorCache() {
  cache.clear();
  pending.clear();
}
